import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';

interface SeoContent {
  pageTitle: string;
  description: string;
  showSummary: boolean;
}

export interface DynamicSeoContent {
  pageTitle: string;
  description: string;
  url?: string;
}

export interface SeoSummaryContent {
  pageTitle: string;
  description: string;
}

interface SeoRouteRule {
  path: string;
  match: 'exact' | 'prefix';
  content: SeoContent;
}

const BRAND_TITLE = '듣고 배우는 한국어';
const PUBLIC_SITE_ORIGIN = 'https://www.listenandlearnkorean.com';
const CANONICAL_PATH_OVERRIDES: Record<string, string> = {
  '/dialect/menu': '/dialect',
  '/saseung/s': '/saseung',
};
const DEFAULT_DESCRIPTION = '6만여 한국어 어휘의 뜻·성조·장단음 표시, 4만 7천여 단어 녹음, 9만 6천여 예문을 제공합니다. 듣고 발음하며 배워 보세요.';
const DYNAMIC_DESCRIPTION_MAX_LENGTH = 80;

// 라우트 컴포넌트가 게시글/콘텐츠 데이터로 SEO 를 직접 갱신하는 경로. 이 경로의 NavigationEnd 에서는
// 정적 매핑(updateByUrl)을 건너뛰어 동적 갱신이 다시 덮여 쓰이지 않게 한다.
const COMPONENT_MANAGED_PATTERNS: RegExp[] = [
  /^\/board\/2\/\d+/,
];

export function normalizeForMeta(value?: string | null): string {
  return (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildDynamicDescription(brief?: string | null, content?: string | null): string {
  const normalizedBrief = normalizeForMeta(brief);
  const source = normalizedBrief || normalizeForMeta(content);

  if (source.length <= DYNAMIC_DESCRIPTION_MAX_LENGTH) {
    return source;
  }

  return `${source.slice(0, DYNAMIC_DESCRIPTION_MAX_LENGTH - 1)}…`;
}

const SEO_RULES: SeoRouteRule[] = [
  {
    path: '/',
    match: 'exact',
    content: {
      pageTitle: '',
      description: '듣고 배우는 한국어 — 6만 여 한국어 어휘의 바른 뜻과 성조, 음가를 배워 보세요.',
      showSummary: true,
    },
  },
  {
    path: '/main',
    match: 'exact',
    content: {
      pageTitle: '메인',
      description: '국어 사전, 옥편, 속담 사전, 사 자 성어 사전, 사투리 사전의 기능을 무료로 누릴 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/word/',
    match: 'prefix',
    content: {
      pageTitle: '단어와 예문',
      description: '궁금한 어휘를 빠르게 검색하고 고저장단이 적용된 녹음을 듣고 정확한 발음을 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/example/',
    match: 'prefix',
    content: {
      pageTitle: '문장 듣기',
      description: '예문을 검색하고 범주·조사별로 살펴보며 문장 속 높낮이와 장단을 들을 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/saseung',
    match: 'exact',
    content: {
      pageTitle: '사 성 옥편',
      description: '평성, 상성, 거성, 입성의 사성 체계 및 옥편의 기능을 활용할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/saseung/',
    match: 'prefix',
    content: {
      pageTitle: '사 성 옥편',
      description: '평성, 상성, 거성, 입성의 사성 체계 및 옥편의 기능을 활용할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/homonym',
    match: 'exact',
    content: {
      pageTitle: '같은 글자, 다른 소리',
      description: '우리말에 많은 동음이의어를 듣고 비교하면서 빠르게 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/sajaseungeo/',
    match: 'prefix',
    content: {
      pageTitle: '사 자 성어',
      description: '약 1,800개 이상의 사 자 성어의 정확한 뜻과 발음, 실 사용의 예를 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/dialect',
    match: 'exact',
    content: {
      pageTitle: '모두 우리말',
      description: '각 지방의 흥미롭고 정겨운 사투리를 전국 9 군데로 나누어 표현과 뜻을 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/dialect/',
    match: 'prefix',
    content: {
      pageTitle: '모두 우리말',
      description: '각 지방의 흥미롭고 정겨운 사투리를 전국 9 군데로 나누어 표현과 뜻을 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/dialect-list/',
    match: 'prefix',
    content: {
      pageTitle: '모두 우리말',
      description: '각 지방의 흥미롭고 정겨운 사투리를 전국 9 군데로 나누어 표현과 뜻을 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/proverb/',
    match: 'prefix',
    content: {
      pageTitle: '속담',
      description: '속담을 재미있게 공부할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/movie-collect/',
    match: 'prefix',
    content: {
      pageTitle: '동영상 모아 보기',
      description: '한국어 학습에 도움이 되는 동영상을 한곳에 모아 볼 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/admin-msg',
    match: 'exact',
    content: {
      pageTitle: '운영자의 말',
      description: '사전 제작 취지와 한국어 표기, 발음, 성조에 관한 운영자의 안내를 볼 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/board/2',
    match: 'exact',
    content: {
      pageTitle: '베풀기',
      description: '베풀기 게시판에서 사용 도움말과 안내 글을 확인할 수 있습니다.',
      showSummary: true,
    },
  },
  {
    path: '/board/2/',
    match: 'prefix',
    content: {
      pageTitle: '베풀기',
      description: '베풀기 게시판에서 사용 도움말과 안내 글을 확인할 수 있습니다.',
      showSummary: true,
    },
  },
];

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly currentSummarySubject = new BehaviorSubject<SeoSummaryContent | null>(null);
  public readonly currentSummary$ = this.currentSummarySubject.asObservable();

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  updateByUrl(url: string): void {
    const pathname = this.toPathname(url);
    const content = this.findContent(pathname);
    this.apply(content, this.toCanonicalUrl(pathname));
  }

  updateDynamic(content: DynamicSeoContent): void {
    const pathname = this.toPathname(content.url ?? this.document.location?.pathname ?? '/');
    this.apply({
      pageTitle: content.pageTitle,
      description: content.description,
      showSummary: true,
    }, this.toCanonicalUrl(pathname));
  }

  isComponentManaged(url: string): boolean {
    const pathname = this.toPathname(url);
    return COMPONENT_MANAGED_PATTERNS.some(pattern => pattern.test(pathname));
  }

  // 라우트 컴포넌트가 SEO 를 직접 갱신하기 전까지 이전 페이지의 summary 가 보이지 않게 한다.
  clearSummaryUntilDynamicUpdate(): void {
    this.currentSummarySubject.next(null);
  }

  private findContent(pathname: string): SeoContent {
    const exactMatch = SEO_RULES.find(rule => rule.match === 'exact' && rule.path === pathname);
    if (exactMatch) {
      return exactMatch.content;
    }

    const prefixMatch = SEO_RULES.find(rule => rule.match === 'prefix' && pathname.startsWith(rule.path));
    if (prefixMatch) {
      return prefixMatch.content;
    }

    return {
      pageTitle: '',
      description: DEFAULT_DESCRIPTION,
      showSummary: false,
    };
  }

  private apply(content: SeoContent, canonicalUrl: string): void {
    const titleText = content.pageTitle ? `${BRAND_TITLE} - ${content.pageTitle}` : BRAND_TITLE;

    this.title.setTitle(titleText);
    this.meta.updateTag({ name: 'description', content: content.description });
    this.meta.updateTag({ property: 'og:title', content: titleText });
    this.meta.updateTag({ property: 'og:description', content: content.description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ name: 'twitter:title', content: titleText });
    this.meta.updateTag({ name: 'twitter:description', content: content.description });
    this.updateCanonical(canonicalUrl);

    this.currentSummarySubject.next(
      content.showSummary
        ? { pageTitle: content.pageTitle, description: content.description }
        : null
    );
  }

  private toPathname(url: string): string {
    const rawPath = url.split('?')[0].split('#')[0] || '/';
    if (rawPath === '/') {
      return rawPath;
    }
    return rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;
  }

  private toCanonicalUrl(pathname: string): string {
    const canonicalPath = CANONICAL_PATH_OVERRIDES[pathname] ?? pathname;
    return canonicalPath === '/' ? PUBLIC_SITE_ORIGIN : `${PUBLIC_SITE_ORIGIN}${canonicalPath}`;
  }

  private updateCanonical(canonicalUrl: string): void {
    let canonicalLink = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = this.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
  }
}
